"use client"

import { useState } from "react"
import { format, startOfWeek, endOfWeek, subDays, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears } from "date-fns"
import { arMA, fr } from "date-fns/locale"
import { CalendarIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useTranslation } from "react-i18next"
import type { DateRange } from "react-day-picker"

interface DateRangePickerProps {
  date: DateRange | undefined
  onDateChange: (date: DateRange | undefined) => void
  placeholder?: string
  className?: string
}

export function DateRangePicker({ date, onDateChange, placeholder, className }: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [showCustom, setShowCustom] = useState(false)
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const locale = isRTL ? arMA : fr

  const formatDateRange = () => {
    if (!date?.from) return placeholder || t("pick_date_range") || "Pick a date range"
    const fromFormatted = format(date.from, "dd/MM/yyyy", { locale })
    if (!date.to) return fromFormatted
    const toFormatted = format(date.to, "dd/MM/yyyy", { locale })
    return isRTL ? `${toFormatted} - ${fromFormatted}` : `${fromFormatted} - ${toFormatted}`
  }

  const handlePresetSelect = (value: string) => {
    const today = new Date()
    let from: Date
    let to: Date

    switch (value) {
      case "last_week":
        from = startOfWeek(subDays(today, 7), { weekStartsOn: isRTL ? 6 : 1 })
        to = endOfWeek(subDays(today, 7), { weekStartsOn: isRTL ? 6 : 1 })
        break
      case "last_month":
        from = startOfMonth(subMonths(today, 1))
        to = endOfMonth(subMonths(today, 1))
        break
      case "last_year":
        from = startOfYear(subYears(today, 1))
        to = endOfYear(subYears(today, 1))
        break
      case "custom":
        setShowCustom(true)
        return
      default:
        return
    }

    setShowCustom(false)
    onDateChange({ from, to })
    setOpen(false)
  }

  const presets = [
    { value: "last_week", label: t('last_week') || 'La semaine dernière' },
    { value: "last_month", label: t('last_month') || 'Le mois dernier' },
    { value: "last_year", label: t('last_year') || "L'année dernière" },
    { value: "custom", label: t('custom') || 'Personnalisé' },
  ]

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-[280px] justify-between font-normal",
            isRTL ? "text-right flex-row-reverse" : "text-left",
            !date?.from && "text-muted-foreground",
            className,
          )}
          onClick={() => setOpen(true)}
        >
          <span>{formatDateRange()}</span>
          <CalendarIcon className="h-4 w-4 shrink-0" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-auto p-0"
        align={isRTL ? "end" : "start"}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className={cn("flex", isRTL ? "flex-col-reverse md:flex-row-reverse" : "flex-col md:flex-row")}>
          {/* Preset sidebar */}
          <div
            className={cn(
              "flex flex-col gap-0.5 p-2 min-w-[160px]",
              isRTL
                ? "border-t md:border-t-0 md:border-l border-border"
                : "border-b md:border-b-0 md:border-r border-border",
            )}
          >
            <span className={cn("text-xs font-medium text-muted-foreground mb-1 px-2", isRTL && "text-right")}>
              {t('filter_by_period') || 'Filtrer par période'}
            </span>
            {presets.map((preset) => (
              <Button
                key={preset.value}
                variant={preset.value === "custom" && showCustom ? "secondary" : "ghost"}
                size="sm"
                className={cn(
                  "w-full text-sm font-normal",
                  isRTL ? "justify-end text-right" : "justify-start text-left",
                )}
                onClick={() => handlePresetSelect(preset.value)}
              >
                {preset.label}
              </Button>
            ))}
          </div>

          {/* Two date inputs for custom range */}
          {showCustom && (
            <div className="flex flex-col gap-3 p-4 min-w-[220px] justify-center">
              <div className="flex flex-col gap-1">
                <label className={cn("text-xs font-medium text-muted-foreground", isRTL && "text-right")}>
                  {t('date_from') || 'Du'}
                </label>
                <input
                  type="date"
                  className="border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  value={date?.from ? format(date.from, 'yyyy-MM-dd') : ''}
                  max={date?.to ? format(date.to, 'yyyy-MM-dd') : undefined}
                  onChange={(e) => {
                    const from = e.target.value ? new Date(e.target.value) : undefined
                    onDateChange({ from, to: date?.to })
                  }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className={cn("text-xs font-medium text-muted-foreground", isRTL && "text-right")}>
                  {t('date_to') || 'Au'}
                </label>
                <input
                  type="date"
                  className="border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  value={date?.to ? format(date.to, 'yyyy-MM-dd') : ''}
                  min={date?.from ? format(date.from, 'yyyy-MM-dd') : undefined}
                  onChange={(e) => {
                    const to = e.target.value ? new Date(e.target.value) : undefined
                    if (date?.from && to) {
                      onDateChange({ from: date.from, to })
                      setOpen(false)
                    } else {
                      onDateChange({ from: date?.from, to })
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
